// Prévisions de demande — table avec tendances et recommandations de commande
import { useState, useMemo } from "react";
import { RefreshCw, TrendingUp, TrendingDown, Minus, ShoppingCart } from "lucide-react";
import AdminLayout from "@/components/admin/AdminLayout";
import { AdminDataTable } from "@/components/admin/shared/AdminDataTable";
import type { ColumnDef } from "@/components/admin/shared/AdminDataTable";
import { EmptyState } from "@/components/admin/shared/EmptyState";
import { AdminModal } from "@/components/admin/shared/AdminModal";
import { useDemandForecast, useRefreshForecast } from "@/hooks/admin/useAI";
import type { ForecastProduct, ForecastTrend } from "@/lib/admin-api/ai";
import React from "react";

// ---------------------------------------------------------------------------
// Formatters
// ---------------------------------------------------------------------------

function formatFcfa(n: number): string {
  return `${n.toLocaleString("fr-SN")} FCFA`;
}

// suppress unused warning — available for use in modal
void formatFcfa;

// ---------------------------------------------------------------------------
// Trend helpers
// ---------------------------------------------------------------------------

const TREND_CONFIG: Record<
  ForecastTrend,
  { Icon: React.ElementType; className: string; label: string }
> = {
  up: { Icon: TrendingUp, className: "text-green-600", label: "Hausse" },
  down: { Icon: TrendingDown, className: "text-red-600", label: "Baisse" },
  stable: { Icon: Minus, className: "text-stone-400", label: "Stable" },
};

function confidenceClass(pct: number): string {
  if (pct >= 80) return "text-green-700 bg-green-50";
  if (pct >= 60) return "text-amber-700 bg-amber-50";
  return "text-red-700 bg-red-50";
}

// ---------------------------------------------------------------------------
// Quick order modal
// ---------------------------------------------------------------------------

interface OrderModalProps {
  readonly product: ForecastProduct | null;
  readonly onClose: () => void;
}

function QuickOrderModal({ product, onClose }: OrderModalProps) {
  const [qty, setQty] = useState(product?.recommended_order_qty ?? 0);

  return (
    <AdminModal
      title={product ? `Commander — ${product.name}` : "Commander"}
      open={Boolean(product)}
      onClose={onClose}
    >
      {product && (
        <div className="space-y-4">
          <div className="rounded-lg bg-stone-50 p-4 text-sm">
            <p className="text-stone-500">
              SKU : <span className="font-medium text-stone-800">{product.sku}</span>
            </p>
            <p className="mt-1 text-stone-500">
              Stock actuel : <span className="font-medium text-stone-800">{product.current_stock}</span>
            </p>
            <p className="mt-1 text-stone-500">
              Point de réappro. : <span className="font-medium text-stone-800">{product.reorder_point}</span>
            </p>
          </div>
          <div>
            <label className="block text-sm font-medium text-stone-700 mb-1">
              Quantité à commander
            </label>
            <input
              type="number"
              min={1}
              value={qty}
              onChange={(e) => setQty(Number(e.target.value))}
              className="w-full rounded-md border border-stone-300 px-3 py-2 text-sm focus:border-stone-500 focus:outline-none"
            />
          </div>
          <div className="flex justify-end gap-2">
            <button
              type="button"
              onClick={onClose}
              className="rounded-md border border-stone-300 px-4 py-2 text-sm font-medium text-stone-700 hover:bg-stone-50"
            >
              Annuler
            </button>
            <button
              type="button"
              onClick={onClose}
              className="rounded-md bg-stone-900 px-4 py-2 text-sm font-medium text-white hover:bg-stone-800"
            >
              Confirmer la commande
            </button>
          </div>
        </div>
      )}
    </AdminModal>
  );
}

// ---------------------------------------------------------------------------
// Page component
// ---------------------------------------------------------------------------

export default function DemandForecast() {
  const [q, setQ] = useState("");
  const [brand, setBrand] = useState("");
  const [orderProduct, setOrderProduct] = useState<ForecastProduct | null>(null);

  const params = useMemo(
    () => ({ q: q || null, brand: brand || null }),
    [q, brand],
  );

  const { data, isLoading, isError } = useDemandForecast(params);
  const refreshMutation = useRefreshForecast();

  const products = data?.products ?? [];

  const columns: readonly ColumnDef<ForecastProduct>[] = useMemo(
    () => [
      {
        id: "sku",
        header: "SKU",
        cell: (row) => <span className="font-mono text-xs text-stone-500">{row.sku}</span>,
      },
      {
        id: "name",
        header: "Produit",
        cell: (row) => (
          <div>
            <p className="text-sm font-medium text-stone-900">{row.name}</p>
            <p className="text-xs text-stone-400">{row.brand}</p>
          </div>
        ),
      },
      {
        id: "current_stock",
        header: "Stock actuel",
        cell: (row) => <span className="text-sm text-stone-700">{row.current_stock}</span>,
      },
      {
        id: "forecast_7d",
        header: "Prév. 7j",
        cell: (row) => <span className="text-sm text-stone-700">{row.forecast_7d}</span>,
      },
      {
        id: "forecast_30d",
        header: "Prév. 30j",
        cell: (row) => <span className="text-sm font-medium text-stone-900">{row.forecast_30d}</span>,
      },
      {
        id: "forecast_90d",
        header: "Prév. 90j",
        cell: (row) => <span className="text-sm text-stone-700">{row.forecast_90d}</span>,
      },
      {
        id: "reorder_point",
        header: "Pt réappro.",
        cell: (row) => <span className="text-sm text-stone-700">{row.reorder_point}</span>,
      },
      {
        id: "recommended_order_qty",
        header: "Qté recommandée",
        cell: (row) => <span className="text-sm font-medium text-stone-900">{row.recommended_order_qty}</span>,
      },
      {
        id: "confidence_percent",
        header: "Confiance",
        cell: (row) => (
          <span className={`inline-block rounded px-2 py-0.5 text-xs font-medium ${confidenceClass(row.confidence_percent)}`}>
            {row.confidence_percent}%
          </span>
        ),
      },
      {
        id: "trend",
        header: "Tendance",
        cell: (row) => {
          const cfg = TREND_CONFIG[row.trend];
          const Icon = cfg.Icon;
          return (
            <span className={`inline-flex items-center gap-1 text-sm font-medium ${cfg.className}`}>
              <Icon className="h-4 w-4" />
              {cfg.label}
            </span>
          );
        },
      },
      {
        id: "actions",
        header: "",
        cell: (row) => (
          <button
            type="button"
            onClick={() => setOrderProduct(row)}
            className="inline-flex items-center gap-1 rounded-md bg-stone-100 px-3 py-1.5 text-xs font-medium text-stone-700 hover:bg-stone-200"
          >
            <ShoppingCart className="h-3.5 w-3.5" />
            Commander
          </button>
        ),
      },
    ],
    [],
  );

  return (
    <AdminLayout>
      <div className="space-y-6 p-6">
        {/* Header */}
        <div className="flex items-start justify-between">
          <div>
            <h1 className="text-2xl font-semibold text-stone-900">Prévisions de demande</h1>
            <p className="mt-1 text-sm text-stone-500">
              Prévisions IA sur 7, 30 et 90 jours avec recommandations de commande
              {data?.generated_at && (
                <span className="ml-2 text-stone-400">
                  — Générées le {new Date(data.generated_at).toLocaleDateString("fr-SN")}
                </span>
              )}
            </p>
          </div>
          <button
            type="button"
            disabled={refreshMutation.isPending}
            onClick={() => refreshMutation.mutate()}
            className="inline-flex items-center gap-2 rounded-md bg-stone-900 px-4 py-2 text-sm font-medium text-white hover:bg-stone-800 disabled:opacity-60"
          >
            <RefreshCw className={`h-4 w-4 ${refreshMutation.isPending ? "animate-spin" : ""}`} />
            Actualiser les prévisions
          </button>
        </div>

        {/* Filters */}
        <div className="flex flex-wrap gap-3">
          <input
            type="search"
            placeholder="Rechercher un produit…"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            className="rounded-md border border-stone-300 px-3 py-2 text-sm focus:border-stone-500 focus:outline-none"
          />
          <input
            type="text"
            placeholder="Filtrer par marque…"
            value={brand}
            onChange={(e) => setBrand(e.target.value)}
            className="rounded-md border border-stone-300 px-3 py-2 text-sm focus:border-stone-500 focus:outline-none"
          />
        </div>

        {/* Table */}
        {isError ? (
          <div className="rounded-lg border border-red-200 bg-red-50 p-4 text-sm text-red-700">
            Impossible de charger les prévisions. Veuillez réessayer.
          </div>
        ) : products.length === 0 && !isLoading ? (
          <EmptyState
            title="Aucune prévision disponible"
            description="Actualisez les prévisions pour obtenir des données."
          />
        ) : (
          <AdminDataTable
            data={products}
            columns={columns}
            isLoading={isLoading}
            emptyMessage="Aucune prévision disponible."
          />
        )}
      </div>

      <QuickOrderModal product={orderProduct} onClose={() => setOrderProduct(null)} />
    </AdminLayout>
  );
}
